/**
 * CodeMirror-based search and replace utilities
 * Mirrors the ProseMirror searchInDocument logic for consistency
 */

import { SearchHighlighterCodeMirror } from '../extensions/codemirror/searchHighlighter.js';

/**
 * Search for text within a CodeMirror document - mirrors ProseMirror searchInDocument
 * @param {EditorView} view - CodeMirror EditorView
 * @param {string} query - Search query
 * @param {Object} options - Search options (caseSensitive, section)
 * @returns {Array} Array of search results with positions
 */
export function searchInCodeMirror(view, query, options = {}) {
    const { caseSensitive = false } = options;
    const results = [];
    
    if (!query || !view || !view.state) return results;
    
    const searchQuery = caseSensitive ? query : query.toLowerCase();
    const doc = view.state.doc;
    const docText = doc.toString();
    const searchText = caseSensitive ? docText : docText.toLowerCase();
    
    let currentSection = { type: 'unsectioned', name: 'Claims' };
    // For claims, we typically search all since no sections - keep for future section support
    
    let index = 0;
    while ((index = searchText.indexOf(searchQuery, index)) !== -1) {
        // Skip inline comments (lines starting with #)
        const lineStart = docText.lastIndexOf('\n', index) + 1;
        const lineText = docText.substring(lineStart, docText.indexOf('\n', index) || docText.length);
        
        // Skip if this match is in an inline comment
        if (lineText.trimStart().startsWith('#')) {
            index += query.length;
            continue;
        }
        
        results.push({
            from: index,
            to: index + query.length,
            text: docText.substring(index, index + query.length),
            section: { ...currentSection },
            editorType: 'claims'
        });
        
        index += query.length;
    }
    
    return results;
}

/**
 * Apply case pattern from original text to replacement - reuses existing logic
 * @param {string} originalText - Original matched text
 * @param {string} replacement - Replacement text
 * @returns {string} Replacement with case pattern applied
 */
export function applyCasePattern(originalText, replacement) {
    if (!originalText || !replacement) return replacement;
    
    // If original is all uppercase, make replacement uppercase
    if (originalText === originalText.toUpperCase() && originalText !== originalText.toLowerCase()) {
        return replacement.toUpperCase();
    }
    
    // If original starts with uppercase (sentence case), capitalize replacement
    if (originalText[0] === originalText[0].toUpperCase() && originalText[0] !== originalText[0].toLowerCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
    }
    
    // Otherwise, keep replacement as lowercase
    return replacement.toLowerCase();
}

/**
 * Replace text at specific position in CodeMirror
 * @param {EditorView} view - CodeMirror EditorView
 * @param {number} from - Start position
 * @param {number} to - End position
 * @param {string} replacement - Replacement text
 * @param {string} originalText - Original text for case pattern
 */
export function replaceTextAtCodeMirror(view, from, to, replacement, originalText) {
    if (!view || !view.state || !view.dispatch) return;
    
    try {
        // Apply case pattern preservation like ProseMirror version
        const finalReplacement = applyCasePattern(originalText, replacement);
        
        view.dispatch({
            changes: {
                from,
                to,
                insert: finalReplacement
            }
        });
    } catch (error) {
        console.error('[CodeMirror Search] Error replacing text:', error);
    }
}

/**
 * Replace all occurrences in CodeMirror
 * @param {EditorView} view - CodeMirror EditorView
 * @param {Array} results - Search results from searchInCodeMirror
 * @param {string} replacement - Replacement text
 * @param {string} section - Section filter
 */
export function replaceAllInCodeMirror(view, results, replacement, section = 'all') {
    if (!view || !view.state || !view.dispatch || !results.length) return;
    
    try {
        // Sort results in reverse order to maintain positions (same as ProseMirror version)
        const sortedResults = [...results].sort((a, b) => b.from - a.from);
        
        // Create changes array for batch update
        const changes = [];
        
        sortedResults.forEach(result => {
            // Only replace if in the correct section
            if (section === 'all' || result.section?.type === section) {
                // Apply case pattern from original text to replacement
                const finalReplacement = applyCasePattern(result.text, replacement);
                changes.push({
                    from: result.from,
                    to: result.to,
                    insert: finalReplacement
                });
            }
        });
        
        // Apply all changes in a single transaction
        if (changes.length > 0) {
            view.dispatch({ changes });
        }
    } catch (error) {
        console.error('[CodeMirror Search] Error replacing all:', error);
    }
}

/**
 * Clear search highlights from CodeMirror
 * @param {EditorView} view - CodeMirror EditorView
 */
export function clearSearchHighlights(view) {
    if (!view || !view.state || !view.dispatch) return;
    
    try {
        SearchHighlighterCodeMirror.clearSearchResults(view);
    } catch (error) {
        console.error('[CodeMirror Search] Error clearing highlights:', error);
    }
}

/**
 * Navigate to a specific position in CodeMirror
 * @param {EditorView} view - CodeMirror EditorView
 * @param {number} from - Position to navigate to
 * @param {boolean} shouldFocus - Whether to focus the editor
 */
export function navigateToPosition(view, from, shouldFocus = false) {
    if (!view || !view.state || !view.dispatch) return;
    
    try {
        // Set cursor position
        view.dispatch({
            selection: { anchor: from },
            scrollIntoView: true
        });
        
        // Focus if requested
        if (shouldFocus) {
            view.focus();
        }
    } catch (error) {
        console.error('[CodeMirror Search] Error navigating to position:', error);
    }
}